import {
  formatIdSchema,
  groupIdSchema,
  matchIdSchema,
  roundKeySchema,
  type GroupId,
  type PhaseId,
  type TeamId,
} from '@/core/schemas';

import type {
  PhaseGroup,
  PhasePlan,
  PlannedMatch,
  Seed,
  StandingsRow,
  StandingsTable,
} from '../../contracts';
import { defineFormat } from '../../defineFormat';
import { toDecidedMatch, type DecidedMatch } from '../../matchResults';

import { groupStageConfigSchema, type GroupStageConfig } from './config';
import { distributeIntoGroups, roundRobinPairings } from './schedule';
import { buildStandings } from './standings';

export const GROUP_STAGE_FORMAT_ID = formatIdSchema.parse('group-stage');

const GROUP_LETTERS = 'abcdefghijklmnopqrstuvwxyz';

function groupIdFor(index: number): GroupId {
  const letter = GROUP_LETTERS[index];
  return groupIdSchema.parse(letter ?? `g${index + 1}`);
}

function groupLabelFor(index: number): string {
  const letter = GROUP_LETTERS[index];
  return `Gruppe ${(letter ?? String(index + 1)).toUpperCase()}`;
}

/** `gruppenphase.spieltag-1` — the key the match rules are resolved with. */
function roundKeyFor(phaseId: PhaseId, round: number) {
  return roundKeySchema.parse(`${phaseId}.spieltag-${round + 1}`);
}

function matchIdFor(phaseId: PhaseId, groupId: GroupId, round: number, index: number) {
  return matchIdSchema.parse(`${phaseId}-${groupId}-t${round + 1}-m${index + 1}`);
}

function decidedMatchesOf(matches: Parameters<typeof toDecidedMatch>[0][]): DecidedMatch[] {
  return matches
    .map((match) => toDecidedMatch(match))
    .filter((match): match is DecidedMatch => match !== null);
}

/**
 * Round-robin groups followed by qualification.
 *
 * Nothing about the number of groups, points, tiebreakers or qualifying places
 * is decided here — all of it arrives as configuration.
 */
export const groupStageFormat = defineFormat<GroupStageConfig>({
  id: GROUP_STAGE_FORMAT_ID,
  label: 'Gruppenphase',
  configSchema: groupStageConfigSchema,

  createPlan({ phase, config, participants }): PhasePlan {
    const distributed = distributeIntoGroups(participants, config.groupCount, config.distribution);

    const groups: PhaseGroup[] = distributed.map((teamIds, index) => ({
      id: groupIdFor(index),
      label: groupLabelFor(index),
      teamIds,
    }));

    const matches: PlannedMatch[] = [];
    let order = 0;

    // Interleaved by round rather than by group: round 1 of every group is
    // played before round 2, which is what keeps the boards evenly busy.
    const schedules = groups.map((group) => ({
      group,
      pairings: roundRobinPairings(group.teamIds, config.doubleRound),
    }));

    const maxRound = Math.max(
      0,
      ...schedules.flatMap(({ pairings }) => pairings.map((pairing) => pairing.round + 1)),
    );

    for (let round = 0; round < maxRound; round += 1) {
      for (const { group, pairings } of schedules) {
        const inRound = pairings.filter((pairing) => pairing.round === round);

        inRound.forEach((pairing, index) => {
          matches.push({
            id: matchIdFor(phase.id, group.id, round, index),
            phaseId: phase.id,
            roundKey: roundKeyFor(phase.id, round),
            groupId: group.id,
            bracketSlot: null,
            order: order++,
            home: { kind: 'team', teamId: pairing.home },
            away: { kind: 'team', teamId: pairing.away },
          });
        });
      }
    }

    return { phaseId: phase.id, groups, matches };
  },

  computeStandings({ phase, config, participants, matches }): StandingsTable[] {
    const decided = decidedMatchesOf([...matches]);
    const distributed = distributeIntoGroups(participants, config.groupCount, config.distribution);

    // Group membership is taken from the planned matches where available, so a
    // manual correction by the tournament director is respected.
    const fromMatches = new Map<GroupId, Set<TeamId>>();
    for (const match of matches) {
      if (match.phaseId !== phase.id || match.groupId === null) {
        continue;
      }
      const members = fromMatches.get(match.groupId) ?? new Set<TeamId>();
      if (match.home.kind === 'team') members.add(match.home.teamId);
      if (match.away.kind === 'team') members.add(match.away.teamId);
      fromMatches.set(match.groupId, members);
    }

    return distributed.map((planned, index) => {
      const groupId = groupIdFor(index);
      const members = fromMatches.get(groupId);
      const teamIds = members === undefined ? planned : [...members];

      return {
        groupId,
        label: groupLabelFor(index),
        rows: buildStandings(teamIds, decided, config),
      };
    });
  },

  /** A group phase has no bracket to advance. */
  resolveProgression() {
    return [];
  },

  selectQualifiers(context): Seed[] {
    const { config } = context;
    const tables = groupStageFormat.computeStandings(context);

    const direct: { row: StandingsRow; table: StandingsTable }[] = [];
    const contenders: { row: StandingsRow; table: StandingsTable }[] = [];

    for (const table of tables) {
      for (const row of table.rows) {
        if (row.rank <= config.qualifiersPerGroup) {
          direct.push({ row, table });
        } else if (row.rank === config.qualifiersPerGroup + 1) {
          contenders.push({ row, table });
        }
      }
    }

    // Group winners first, then all runners-up, and so on — the usual way to
    // keep teams from the same group apart in the bracket.
    direct.sort((a, b) => a.row.rank - b.row.rank || a.table.label.localeCompare(b.table.label));

    const best = [...contenders]
      .sort(
        (a, b) =>
          b.row.points - a.row.points ||
          b.row.legDifference - a.row.legDifference ||
          b.row.legsFor - a.row.legsFor ||
          a.row.teamId.localeCompare(b.row.teamId),
      )
      .slice(0, config.additionalQualifiers);

    return [...direct, ...best].map((entry, index) => ({
      teamId: entry.row.teamId,
      rank: index + 1,
      label: `${entry.table.label}, Platz ${entry.row.rank}`,
    }));
  },

  isComplete({ phase, matches }): boolean {
    const own = matches.filter((match) => match.phaseId === phase.id);
    return own.length > 0 && own.every((match) => match.status === 'completed');
  },
});
